import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { useSaveToast } from '../ui/SaveToast';
import AiFeatureCompartment from './AiFeatureCompartment';
import LabeledToggle from './LabeledToggle';
import { D2_INPUT } from '../settings/signatures/d2Fields';
import { describePause, fetchAiIntegration, hasOpenHours, saveAiIntegration, saveTestContacts } from '../../lib/businessSchedule';

// BL-177 Step 2m: the AI Assistant's compartment (Admin Center › Business schedule › AI integration).
// The shared pattern (AiFeatureCompartment) plus this feature's own options:
//   Name your assistant     the name it uses with clients
//   Cover after-hours       on = answers only outside the open hours above; off = around the clock
//   Auto-reserve            books the trip itself only when every detail is complete, otherwise dispatch gets "Needs info"
//   Channels                Text, WhatsApp, Email in one row
export default function AiAssistantCompartment({ orgId, hoursByDay = {}, client = supabaseClient, reloadKey = 0, onChanged, defaultRevealed = false }) {
    const toast = useSaveToast();
    const [settings, setSettings] = useState(null);
    const [name, setName] = useState('');
    const [loadError, setLoadError] = useState('');

    const load = useCallback(async () => {
        try {
            const next = await fetchAiIntegration(client, orgId);
            setSettings(next);
            setName(next?.assistant_name || '');
            setLoadError('');
        } catch (error) {
            setLoadError(error?.message || 'The AI settings could not be loaded.');
        }
    }, [client, orgId]);
    useEffect(() => { load(); }, [load, reloadKey]);

    // Optimistic save of one option: show it now, write it, toast the result, put it back if the write fails.
    async function change(patch) {
        const before = settings;
        setSettings((current) => ({ ...current, ...patch }));
        try {
            const saved = await saveAiIntegration(client, orgId, patch);
            setSettings(saved);
            toast.saved();
            onChanged?.(saved);
        } catch (error) {
            setSettings(before);
            toast.failed(`Couldn't save. Switched back. ${error?.message || ''}`.trim());
        }
    }

    async function setStatus(status) {
        const saved = await saveAiIntegration(client, orgId, { status });
        setSettings(saved);
        toast.saved();
        onChanged?.(saved);
    }

    async function saveList(entries) {
        await saveTestContacts(client, orgId, entries, settings?.test_contacts || []);
        await load();
    }

    if (loadError) return <p role="alert" className="text-sm font-bold text-red-600">{loadError}</p>;
    if (!settings) return <p className="text-sm text-[var(--ink-muted)]">Loading…</p>;

    const channels = settings.channels || { sms: true, whatsapp: true, email: true };
    return (
        <AiFeatureCompartment
            id="ai-integration"
            title="AI integration"
            description="Your AI Assistant answers clients for you. Choose how it should run."
            status={settings.status}
            testContacts={settings.test_contacts || []}
            pausedLine={describePause(settings)}
            onSetStatus={setStatus}
            onSaveTestContacts={saveList}
            defaultRevealed={defaultRevealed}
        >
            <label className="block">
                <span className="mb-1 block text-sm font-bold text-white">Name your assistant</span>
                <input
                    type="text" value={name} maxLength={40} placeholder="For example: Riley"
                    onChange={(event) => setName(event.target.value)}
                    onBlur={() => { if (name.trim() !== (settings.assistant_name || '')) change({ assistant_name: name.trim() }); }}
                    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                    className={`${D2_INPUT} w-full max-w-sm`}
                    aria-describedby="ai-name-help"
                />
                <span id="ai-name-help" className="mt-1 block text-xs text-[#C7D2E8]">The name the assistant uses with your clients.</span>
            </label>
            <LabeledToggle
                label="Cover after-hours"
                hint="On: answers only outside your open hours. Off: answers around the clock."
                checked={settings.cover_after_hours !== false}
                onChange={(next) => change({ cover_after_hours: next })}
            />
            <label className="block text-sm font-bold text-white">
                After staff take a thread, pause the AI for
                <select
                    aria-label="After staff take a thread, pause the AI for"
                    value={settings.takeover_pause_minutes === null ? 'until_closed' : String(settings.takeover_pause_minutes ?? 240)}
                    onChange={(event) => change({
                        takeover_pause_minutes: event.target.value === 'until_closed' ? null : Number(event.target.value),
                    })}
                    className={`${D2_INPUT} mt-2 w-full max-w-sm`}
                >
                    <option value="60">60 minutes</option>
                    <option value="240">240 minutes (default)</option>
                    <option value="1440">1440 minutes</option>
                    <option value="until_closed">Until staff close the thread</option>
                </select>
            </label>
            {settings.cover_after_hours !== false && !hasOpenHours(hoursByDay) && (
                <p role="note" className="rounded-lg border border-amber-500 bg-[#2A1E08] px-3 py-2 text-xs font-bold text-white">
                    You have not saved any open hours yet, so there is no "after hours" to cover and the assistant will stay quiet. Set your open hours above.
                </p>
            )}
            <LabeledToggle
                label="Auto-reserve"
                hint={'Books the trip itself only when every detail is complete. Otherwise it goes to dispatch as "Needs info".'}
                checked={settings.auto_reserve !== false}
                onChange={(next) => change({ auto_reserve: next })}
            />
            <fieldset className="space-y-2">
                <legend className="text-sm font-bold text-white">Channels</legend>
                <div className="flex flex-wrap gap-x-8 gap-y-3" data-testid="ai-channels">
                    {[['sms', 'Text'], ['whatsapp', 'WhatsApp'], ['email', 'Email']].map(([key, label]) => (
                        <LabeledToggle key={key} inline label={label} checked={channels[key] === true}
                            onChange={(next) => change({ channels: { ...channels, [key]: next } })} />
                    ))}
                </div>
            </fieldset>
        </AiFeatureCompartment>
    );
}
